/**
 * Wend-owned links and postbacks — slice 63.
 *
 * ## THE CLICK ID
 *
 * Every tracked link in a campaign email leads through Wend's redirect, which
 * records the first click by that person on that link and sends them on with
 * `?wend_click=<id>` added to the destination. A website that keeps that id —
 * in a form field, a cookie, an order note — can post a conversion back
 * against it. Wend knows who clicked, so the conversion lands as Matched: a
 * person on a day, with no spreadsheet.
 *
 * ## THE POSTBACK
 *
 * `POST /api/public/conversions`, JSON, signed: the header
 * {@link POSTBACK_SIGNATURE_HEADER} carries `sha256=` and the hex HMAC-SHA-256
 * of the exact request body under the workspace's postback key. The key is
 * shown once, when it is made, and can be replaced; the old one stops working
 * at that moment.
 *
 * One conversion per person, type and day, as for an uploaded list: a page that
 * posts twice records once, and the second answer says so.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { calendarDateSchema } from './analytics.js';

export const CLICK_ID_PARAM = 'wend_click';
export const POSTBACK_SIGNATURE_HEADER = 'x-wend-signature';
/** Most rupees one postback may carry. A conversion worth more is a typo. */
export const POSTBACK_MAX_RUPEES = 100_000_000;

export const postbackRequestSchema = z
  .object({
    /** The workspace id, shown beside the key. */
    workspace: uuidSchema,
    /** The `wend_click` value the website received. */
    clickId: uuidSchema,
    /** Which conversion it was — an outcome type's id, from the Outcomes page. */
    outcomeTypeId: uuidSchema,
    /** In rupees; the outcome type's value when absent. */
    value: z.number().min(0).max(POSTBACK_MAX_RUPEES).optional(),
    /** The day it happened; today (India) when absent. Never in the future. */
    occurredOn: calendarDateSchema.optional(),
  })
  .strict();
export type PostbackRequest = z.infer<typeof postbackRequestSchema>;

export const postbackResponseSchema = z
  .object({
    recorded: z.boolean(),
    /** Present when nothing new was recorded: the same person, type and day already were. */
    reason: z.literal('already_recorded').optional(),
  })
  .strict();
export type PostbackResponse = z.infer<typeof postbackResponseSchema>;

/** `GET /sense/postback-key` — whether one exists. The key itself is never read back. */
export const postbackKeyStatusSchema = z
  .object({
    workspaceId: uuidSchema,
    exists: z.boolean(),
    createdAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type PostbackKeyStatus = z.infer<typeof postbackKeyStatusSchema>;

/** `POST /sense/postback-key` — a new key, shown this once. */
export const postbackKeyCreatedSchema = z
  .object({
    workspaceId: uuidSchema,
    key: z.string().min(32),
    createdAt: isoDateTimeSchema,
  })
  .strict();
export type PostbackKeyCreated = z.infer<typeof postbackKeyCreatedSchema>;
